/**
 * Language switcher — all 8 EU locales: en, de, fr, nl, pl, sv, no, da.
 */
import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LOCALES } from '../../i18n/config.js';
import { Popover } from './Popover.js';

export function LanguageSwitcher({ className = '' }: { className?: string }) {
  const { i18n } = useTranslation();
  const [open, setOpen]   = useState(false);
  const buttonRef         = useRef<HTMLButtonElement>(null);

  const current = SUPPORTED_LOCALES.find(l => l.code === i18n.resolvedLanguage)
    ?? SUPPORTED_LOCALES[0];

  return (
    <div className={`relative ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid="language-switcher"
        className="flex h-8 items-center gap-1.5 rounded-md px-2
                   text-sm text-[var(--nord-subtext)] transition-colors
                   hover:bg-[var(--nord-surface)] hover:text-[var(--nord-text)]"
      >
        <span aria-hidden="true">{current.flag}</span>
        <span className="hidden sm:inline">{current.code.toUpperCase()}</span>
        <svg className="h-3 w-3" viewBox="0 0 12 12" fill="currentColor">
          <path d="M6 8L1 3h10z" />
        </svg>
      </button>

      <Popover
        open={open}
        anchorRef={buttonRef}
        onClose={() => setOpen(false)}
        offset={4}
        className="w-44 rounded-lg border border-[var(--nord-border)] bg-[var(--nord-surface)] shadow-lg"
      >
          {SUPPORTED_LOCALES.map(locale => (
            <button
              key={locale.code}
              type="button"
              onClick={() => { i18n.changeLanguage(locale.code); setOpen(false); }}
              data-testid={`lang-${locale.code}`}
              className={[
                'flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors',
                'hover:bg-[var(--nord-bg)] hover:text-[var(--nord-text)]',
                i18n.resolvedLanguage === locale.code
                  ? 'text-[var(--nord-accent)] font-medium'
                  : 'text-[var(--nord-subtext)]',
              ].join(' ')}
            >
              <span>{locale.flag}</span>
              <span>{locale.label}</span>
            </button>
          ))}
      </Popover>
    </div>
  );
}
